import {
  Box,
  Button,
  chakra,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  Heading,
  Text,
} from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../../lib/apiReq";
import {
  NUM_LIKES_BEFORE_UPDATE,
  OneTimeMessageId,
  UserSparkWithRegistrationSummary,
} from "@greenflagdate/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  MatchProfile,
} from "../Match/MatchProfile";
import {
  ScreenTransitioner,
} from "../../../components/ScreenTransitioner/ScreenTransitioner";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { useLocation, useNavigate } from "react-router-dom";
import { PreSwipeEmptyState } from "./PreSwipeEmptyState";
import { useProfileDeck } from "../../../lib/useProfileDeck";
import { FriendsModeState } from "./FriendsModeState";
import mobile from "is-mobile";
import {
  CompleteProfileGate,
  getMissingProfileSteps,
} from "../../../components/CompleteProfileGate/CompleteProfileGate";
import { Icon } from "../../../components/Icon/Icon";
import { PostEventMatchNudge } from "../../../components/PostEventMatchNudge/PostEventMatchNudge";
import { hasPostEventMatchNudge } from "../../../components/PostEventMatchNudge/hasPostEventMatchNudge";
import { formatEventWhen } from "../../../lib/formatEventWhen";

// Id for the first-visit /spark explainer in users.one_time_messages_seen.
// Persisted server-side so dismissals follow the user across devices.
const SPARK_INTRO_MESSAGE_ID: OneTimeMessageId = "spark-intro";

function LikesAttendingChip({ count }: { count: number }) {
  return (
    <Flex
      align="center"
      gap="1"
      fontSize="xs"
      fontWeight="semibold"
      color="fg.brand"
      bg="bg.canvas"
      px="2"
      py="1"
      borderRadius="full"
      flexShrink={0}
    >
      <Icon name="spark-filled" boxSize="10px" />
      {count} liked
    </Flex>
  );
}

function LikedEventRow({
  event,
  likesAttending,
  onClick,
}: {
  event: UserSparkWithRegistrationSummary;
  likesAttending: number;
  onClick: () => void;
}) {
  const image = event.primary_image || event.hero_image;
  const label = event.title || event.activity;
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      gap="0.75rem"
      w="100%"
      textAlign="left"
      bg="transparent"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="md"
      px="0.75rem"
      py="0.625rem"
      cursor="pointer"
      _hover={{ bg: "bg.subtle" }}
    >
      {image && (
        <Box
          w="3rem"
          h="3rem"
          borderRadius="md"
          flexShrink={0}
          bg="bg.subtle"
          backgroundImage={`url(${image})`}
          backgroundSize="cover"
          backgroundPosition="center"
        />
      )}
      <Flex direction="column" flex="1" minW="0">
        <Text fontWeight="semibold" truncate>
          {label}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {formatEventWhen(event.starts_at)}
        </Text>
      </Flex>
      {likesAttending > 0 && <LikesAttendingChip count={likesAttending} />}
    </chakra.button>
  );
}

export const Spark = () => {
  const storeData = useStoreData();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Spark is kept mounted under KeepAliveTabs even when its tab is hidden, so
  // the gate must opt in only when /spark is the active route — otherwise it
  // would surface on /event/:sparkId/match (which has its own dismissible gate).
  const sparkTabActive = pathname === "/spark";
  // When the viewer has an event with unfinished post-event matching, the tab
  // shows a blocking interstitial in place of the swipe deck (users were
  // confusing pre-event Sparking here with post-event matching). Only on the
  // active /spark route — the deck stays reachable everywhere else this
  // component is kept mounted.
  // Friends Only takes the viewer out of everyone else's deck, so there's no
  // deck to show them either. Everything dating-shaped on this page — the
  // profile fetch, the complete-your-profile gate, the first-visit intro — is
  // skipped in favor of FriendsModeState, which offers the switch inline.
  const friendsOnly = storeData.user?.friends_only ?? false;
  // Declared after friendsOnly because the nudge is mode-aware: a Friends Only
  // viewer has unfinished matching when FRIEND-category attendees are still
  // undecided. It takes priority over FriendsModeState below — post-event
  // matching is the one dating-adjacent surface they do belong on.
  const showMatchNudge =
    sparkTabActive && hasPostEventMatchNudge(storeData.pastSparks, friendsOnly);
  const [eventsDialogVisible, setEventsDialogVisible] = useState(false);
  // First-visit explainer is gated on users.one_time_messages_seen — we open
  // the modal when the user has loaded and "spark-intro" isn't in the list.
  // Dismissal POSTs the id, optimistically updates the store, and never asks
  // again on any device.
  const seenSparkIntro =
    storeData.user?.one_time_messages_seen?.includes(SPARK_INTRO_MESSAGE_ID) ??
    // Default to "seen" while the user is still loading so the modal doesn't
    // flicker in over the loader. Once /user/state lands the real value flips
    // it open if needed.
    true;
  const [introVisible, setIntroVisible] = useState(false);
  useEffect(() => {
    if (!seenSparkIntro) setIntroVisible(true);
  }, [seenSparkIntro]);
  const [introSeenReq] = useReq<{ one_time_messages_seen: OneTimeMessageId[] }>(
    apiReq
  );
  const dismissIntro = useCallback(async () => {
    setIntroVisible(false);
    if (!storeData.user) return;
    // Optimistically mark seen in the store so a rerender during the in-flight
    // POST doesn't reopen the modal. The server response is authoritative —
    // we replace with its array on success to pick up any other ids that may
    // have been added in another tab.
    const optimistic = storeData.user.one_time_messages_seen?.includes(
      SPARK_INTRO_MESSAGE_ID
    )
      ? storeData.user.one_time_messages_seen
      : [
          ...(storeData.user.one_time_messages_seen ?? []),
          SPARK_INTRO_MESSAGE_ID,
        ];
    store.dispatch({
      type: ActionType.UpdateUser,
      params: {
        user: { ...storeData.user, one_time_messages_seen: optimistic },
      },
    });
    const result = await introSeenReq.post("/user/one-time-messages-seen", {
      messageId: SPARK_INTRO_MESSAGE_ID,
    });
    if (result.success && storeData.user) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: {
          user: {
            ...storeData.user,
            one_time_messages_seen: result.result.one_time_messages_seen,
          },
        },
      });
    }
  }, [introSeenReq, storeData.user]);
  const isMobile = useMemo(() => mobile(), []);
  // Open the "People you like are going to these events!" dialog on exactly
  // the NUM_LIKES_BEFORE_UPDATE-th PreSpark of the session. Tied to the local
  // click counter (not the server's cache flush) so the dialog timing stays
  // predictable for users.
  const onPreSpark = useCallback((numRecentLikes: number) => {
    if (numRecentLikes === NUM_LIKES_BEFORE_UPDATE) {
      setEventsDialogVisible(true);
    }
  }, []);
  const {
    setRelationship,
    screenTransitionerRef,
    deckScreens,
    gateRequiredSteps,
    matchCelebration,
  } = useProfileDeck({
    enabled: !friendsOnly,
    shown: sparkTabActive,
    onPreSpark,
  });
  const unregisteredWithLikes = useMemo(() => {
    const sparks = storeData.upcomingSparks ?? [];
    return sparks.filter(
      (s) =>
        !s.registered &&
        (storeData.user?.likes_attending_sparks_cache[s.id] || 0) > 0
    );
  }, [storeData.upcomingSparks, storeData.user?.likes_attending_sparks_cache]);
  // Mirror the gate's own visibility logic so we know whether to nest the
  // intro inside it or render the intro as its own modal.
  const gateWillShow =
    sparkTabActive &&
    !friendsOnly &&
    getMissingProfileSteps(storeData.user, [...gateRequiredSteps]).length > 0;
  const renderIntroContent = (onDismiss: () => void) => (
    <Flex direction="column" flex={1} minH={0}>
      <Box flex={1} minH={0} overflowY="auto" px="6" pt="6" pb="4">
        <Flex direction="column" align="center" textAlign="center" gap="0.75rem" mb="1rem">
          <Flex
            align="center"
            justify="center"
            boxSize="3rem"
            borderRadius="full"
            bg="bg.brand"
            color="fg.onBrand"
          >
            <Icon name="spark-filled" boxSize="1.5rem" />
          </Flex>
          <Heading as="span" textStyle="headingBold">
            Spark Before You Meet ✨
          </Heading>
        </Flex>
        <Flex direction="column" gap="1rem" fontSize="md">
          <Text color="fg.muted">
            Spark people you'd want to meet. We use it to recommend events and
            curate introductions — so you walk in already excited about who
            you'll meet.
          </Text>
          <Box>
            <Text fontWeight="semibold" color="fg" mb="0.375rem">
              How it works
            </Text>
            <Flex
              as="ul"
              direction="column"
              gap="0.25rem"
              color="fg.muted"
              pl="1.125rem"
              css={{ listStyleType: "disc" }}
            >
              <chakra.li>Spark anyone you'd want to meet</chakra.li>
              <chakra.li>
                Get event recs based on who your Sparks are attending
              </chakra.li>
              <chakra.li>
                Meet them through curated intros at the event
              </chakra.li>
            </Flex>
          </Box>
        </Flex>
      </Box>
      <Box
        flexShrink={0}
        px="6"
        pt="3"
        pb="calc(12px + env(safe-area-inset-bottom))"
        bg="bg.canvas"
        borderTop="1px solid"
        borderColor="border.subtle"
      >
        <Button
          w="100%"
          bg="bg.brand"
          color="fg.onBrand"
          onClick={() => {
            void dismissIntro();
            onDismiss();
          }}
        >
          Got it
        </Button>
      </Box>
    </Flex>
  );
  return (
    <Box paddingX="1rem">
      {showMatchNudge ? (
        <Box maxWidth="40rem" marginX="auto">
          <PostEventMatchNudge />
        </Box>
      ) : friendsOnly ? (
        <Box maxWidth="40rem" marginX="auto">
          <FriendsModeState
            minHeight={
              isMobile ? "calc(100vh - 4.5rem)" : "calc(100vh - 16.85rem)"
            }
          />
        </Box>
      ) : (
      <Box maxWidth="40rem" position="relative" marginX="auto">
        <ScreenTransitioner
          ref={screenTransitionerRef}
          minHeight={
            isMobile ? "calc(100vh - 4.5rem)" : "calc(100vh - 16.85rem)"
          }
          screens={{
            ...deckScreens((profile, behind) => (
              <Box marginX="-1rem" width="calc(100% + 2rem)">
                <MatchProfile
                  profile={profile}
                  behindProfile={behind}
                  onDecision={setRelationship}
                  decisionButtonsBottomOffset="4rem"
                />
              </Box>
            )),
            noMoreProfiles: (
              <PreSwipeEmptyState
                maxWidth="25rem"
                marginX="auto"
                width="100%"
                display="flex"
                flexDirection="column"
                position="relative"
                justifyContent="center"
              />
            ),
          }}
          startIndex="loader"
        />
      </Box>
      )}
      {sparkTabActive && !friendsOnly && (
        <CompleteProfileGate
          requiredSteps={[...gateRequiredSteps]}
          dismissible={false}
          intro={
            introVisible
              ? { render: renderIntroContent }
              : undefined
          }
        />
      )}
      {sparkTabActive && !gateWillShow && !showMatchNudge && !friendsOnly && (
      <DialogRoot
        open={introVisible}
        onOpenChange={(details) => {
          if (!details.open) dismissIntro();
        }}
        closeOnInteractOutside={false}
        closeOnEscape={false}
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent borderRadius="1rem" maxW="28rem">
            <DialogHeader
              px="1.5rem"
              pt="1.5rem"
              pb="0.5rem"
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              gap="0.75rem"
            >
              <Flex
                align="center"
                justify="center"
                boxSize="3rem"
                borderRadius="full"
                bg="bg.brand"
                color="fg.onBrand"
              >
                <Icon name="spark-filled" boxSize="1.5rem" />
              </Flex>
              <DialogTitle>
                <Heading as="span" textStyle="headingBold">
                  Spark Before You Meet ✨
                </Heading>
              </DialogTitle>
            </DialogHeader>
            <DialogBody px="1.5rem" pb="1.5rem">
              <Flex direction="column" gap="1rem" fontSize="md">
                <Text color="fg.muted">
                  Spark people you'd want to meet. We use it to recommend
                  events and curate introductions — so you walk in already
                  excited about who you'll meet.
                </Text>
                <Box>
                  <Text fontWeight="semibold" color="fg" mb="0.375rem">
                    How it works
                  </Text>
                  <Flex
                    as="ul"
                    direction="column"
                    gap="0.25rem"
                    color="fg.muted"
                    pl="1.125rem"
                    css={{ listStyleType: "disc" }}
                  >
                    <chakra.li>Spark anyone you'd want to meet</chakra.li>
                    <chakra.li>
                      Get event recs based on who your Sparks are attending
                    </chakra.li>
                    <chakra.li>
                      Meet them through curated intros at the event
                    </chakra.li>
                  </Flex>
                </Box>
                <Button
                  w="100%"
                  bg="bg.brand"
                  color="fg.onBrand"
                  onClick={dismissIntro}
                >
                  Got it
                </Button>
              </Flex>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
      )}
      {sparkTabActive && (
      <DialogRoot
        open={eventsDialogVisible && unregisteredWithLikes.length > 0}
        onOpenChange={(details) => {
          if (!details.open) setEventsDialogVisible(false);
        }}
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent borderRadius="1rem" maxW="32rem">
            <DialogHeader
              px="1.5rem"
              pt="1.25rem"
              pb="0.75rem"
              borderBottom="1px solid"
              borderColor="border.subtle"
            >
              <DialogTitle>
                <Heading as="span" textStyle="headingBold">
                  People you like are going to these events!
                </Heading>
              </DialogTitle>
            </DialogHeader>
            <DialogBody p="1rem">
              <Flex direction="column" gap="1rem">
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="fg.muted"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                    mb="0.5rem"
                  >
                    Register so you can match
                  </Text>
                  <Flex direction="column" gap="0.5rem">
                    {unregisteredWithLikes.map((event) => (
                      <LikedEventRow
                        key={event.id}
                        event={event}
                        likesAttending={
                          storeData.user?.likes_attending_sparks_cache[
                            event.id
                          ] || 0
                        }
                        onClick={() => navigate(`/event/atlanta/${event.id}`)}
                      />
                    ))}
                  </Flex>
                </Box>
                <Button
                  w="100%"
                  bg="bg.brand"
                  color="fg.onBrand"
                  onClick={() => navigate("/events")}
                >
                  View all events
                </Button>
              </Flex>
            </DialogBody>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
      )}
      {matchCelebration}
    </Box>
  );
};
